import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { debounce } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import CohortAddVmComponent from "./component";

const CohortAddVMContainer = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [seedData, setSeedData] = useState({ users: [] });
  const [selectedVM, setSelectedVM] = useState([]);
  const [errors, setErrors] = useState({});
  const [cohortDetailsData, setCohortDetailsData] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [vmSearchInput, setVmSearchInput] = useState("");
  const [pasteStatus, setPasteStatus] = useState(null);
  const [allVMs, setAllVMs] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [lastPasteIds, setLastPasteIds] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const tableContainerRef = useRef(null);

  // Memoize existing VM IDs to avoid recalculating
  const existingVMIds = useMemo(() => 
    cohortDetailsData?.machines?.map(vm => vm.id.toString()) || [], 
    [cohortDetailsData]
  );

  // Get existing VMs from cohort data
  const existingVMs = useMemo(() => 
    cohortDetailsData?.machines || [], 
    [cohortDetailsData]
  );

  // Load cohort details
  const LoadCohortViewDetails = useCallback(() => {
    props.getDataFromAPI(
      `/dashboard/api/v2/admin/vendingmachines/vm-cohorts/${id}`,
      "GET",
      undefined,
      (response) => {
        setCohortDetailsData(response);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        toastr.error("Error", "Failed to load cohort details");
      },
      true
    );
  }, [id, props.getDataFromAPI]);

  // Load seed data
  const handleGetSeedData = useCallback(() => {
    const url = `/dashboard/api/v2/admin/vendingmachines/vm-cohorts/seed`;

    props.getDataFromAPI(
      url,
      "GET",
      undefined,
      (response) => {
        console.log("Seed response:", response);
        setSeedData(response);
        
        // Sort VMs by ID when setting allVMs
        if (response?.machines) {
          const sortedVMs = [...response.machines].sort((a, b) => a.id - b.id);
          setAllVMs(sortedVMs);
          
          // Prepare initial filtered data with existing VMs first
          prepareFilteredTableData(sortedVMs, "", []);
        }
        setLoading(false);
        setIsInitialLoad(false);
      },
      (err) => {
        console.log("Error:", err);
        toastr.error("Failed", "Unable to fetch seed data");
        setLoading(false);
        setIsInitialLoad(false);
      }
    );
  }, [props.getDataFromAPI]);

  // Prepare filtered table data with existing VMs first, then new selections
  const prepareFilteredTableData = useCallback((allVMsList, searchValue, currentSelectedVM) => {
    if (!searchValue.trim() || allVMsList.length === 0) {
      // Create combined list: existing VMs first, then all other VMs
      const combinedList = [
        ...existingVMs, // Already in cohort - shown first
        ...allVMsList.filter(vm => 
          !existingVMs.some(existing => existing.id === vm.id)
        )
      ].sort((a, b) => {
        // Sort within sections: selected first, then by ID
        const aInExisting = existingVMs.some(existing => existing.id === a.id);
        const bInExisting = existingVMs.some(existing => existing.id === b.id);
        
        // Existing VMs should always come first
        if (aInExisting && !bInExisting) return -1;
        if (!aInExisting && bInExisting) return 1;
        
        // Within same section, sort by selection status then ID
        const aSelected = currentSelectedVM.some(selected => selected.id === a.id) || aInExisting;
        const bSelected = currentSelectedVM.some(selected => selected.id === b.id) || bInExisting;
        
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        
        return a.id - b.id;
      });
      
      setFilteredTableData(combinedList);
      return;
    }

    const searchLower = searchValue.toLowerCase();
    
    // Start search loading indicator
    setSearchLoading(true);
    
    // Use setTimeout to allow UI to update before heavy computation
    setTimeout(() => {
      let filteredData = [];
      
      // Check for comma-separated IDs
      if (searchValue.includes(',')) {
        const searchIds = searchValue
          .split(',')
          .map(id => id.trim().replace(/^0+/, ''))
          .filter(id => id !== '' && /^\d+$/.test(id));
        
        if (searchIds.length > 0) {
          // Convert to Set for faster lookup
          const searchIdSet = new Set(searchIds);
          
          // Filter using Set.has() for O(1) lookups
          filteredData = allVMsList.filter(machine => 
            searchIdSet.has(machine.id.toString())
          );
        }
      } else {
        // Regular text search - optimized
        filteredData = allVMsList.filter(machine => {
          const machineId = machine.id.toString();
          const machineName = machine.name?.toLowerCase() || '';
          
          // Fast check: if ID starts with search term
          if (machineId.startsWith(searchLower)) {
            return true;
          }
          
          // Check if search term is contained in ID or name
          return machineId.includes(searchLower) || 
                 machineName.includes(searchLower);
        });
      }
      
      // Sort results with existing VMs first
      filteredData.sort((a, b) => {
        const aInExisting = existingVMs.some(existing => existing.id === a.id);
        const bInExisting = existingVMs.some(existing => existing.id === b.id);
        
        // Existing VMs should always come first
        if (aInExisting && !bInExisting) return -1;
        if (!aInExisting && bInExisting) return 1;
        
        // Within same section, sort by selection status then ID
        const aSelected = currentSelectedVM.some(selected => selected.id === a.id) || aInExisting;
        const bSelected = currentSelectedVM.some(selected => selected.id === b.id) || bInExisting;
        
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        
        return a.id - b.id;
      });
      
      setFilteredTableData(filteredData);
      setSearchLoading(false);
    }, 10); // Small delay to allow UI thread to breathe
  }, [existingVMs]);

  // Initial load
  useEffect(() => {
    handleGetSeedData();
    LoadCohortViewDetails();
  }, [handleGetSeedData, LoadCohortViewDetails]);

  // Update filtered data when selections change
  useEffect(() => {
    if (allVMs.length > 0 && !isInitialLoad) {
      prepareFilteredTableData(allVMs, vmSearchInput, selectedVM);
    }
  }, [selectedVM, allVMs, vmSearchInput, prepareFilteredTableData, isInitialLoad]);

  // Debounced search handler
  const debouncedSearch = useMemo(
    () => debounce((searchValue) => {
      prepareFilteredTableData(allVMs, searchValue, selectedVM);
    }, 300), // 300ms delay
    [prepareFilteredTableData, allVMs, selectedVM]
  );

  // Handle search input change
  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    setVmSearchInput(value);
    setPasteStatus(null);
    
    // Trigger debounced search
    debouncedSearch(value);
  }, [debouncedSearch]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setVmSearchInput("");
    setPasteStatus(null);
    prepareFilteredTableData(allVMs, "", selectedVM);
  }, [prepareFilteredTableData, allVMs, selectedVM]);

  // Handle bulk VM IDs paste
  const handleBulkVMPaste = useCallback((e) => {
    e.preventDefault();
    setPasteStatus(null);
    setSearchLoading(true);

    // Get raw data from clipboard
    let pasteData = e.clipboardData.getData("Text");
    
    // Set the input value
    setVmSearchInput(pasteData);

    // Process the pasted data
    const processedData = pasteData
      .trim()
      .replace(/[\n\r\t]+/g, ",")
      .replace(/\s+/g, ",")
      .replace(/,+/g, ",");

    // Extract valid IDs
    const ids = processedData
      .split(",")
      .map((id) => id.replace(/^0+/, "").trim())
      .filter((id) => id.length > 0 && /^\d+$/.test(id));

    if (ids.length === 0) {
      setPasteStatus({
        severity: "warning",
        message: "No valid VM IDs found in pasted text"
      });
      setSearchLoading(false);
      prepareFilteredTableData(allVMs, "", selectedVM);
      return;
    }

    // Store paste IDs
    setLastPasteIds(ids);

    // Show processing status
    setPasteStatus({
      severity: "info",
      message: `Processing ${ids.length} VM ID(s)...`
    });

    // Process in next tick to avoid blocking UI
    setTimeout(() => {
      let addedCount = 0;
      let alreadyInCohortCount = 0;
      let alreadySelectedCount = 0;
      let notFoundCount = 0;

      // Create a Set for faster lookups
      const existingIdsSet = new Set(existingVMIds);
      const selectedIdsSet = new Set(selectedVM.map(vm => vm.id.toString()));
      
      // Convert allVMs to Map for O(1) lookups
      const vmMap = new Map();
      allVMs.forEach(vm => {
        vmMap.set(vm.id.toString(), vm);
      });

      const newSelectedVMs = [...selectedVM];
      
      ids.forEach((id) => {
        // Check if already in cohort
        if (existingIdsSet.has(id)) {
          alreadyInCohortCount++;
          return;
        }

        // Check if already selected
        if (selectedIdsSet.has(id)) {
          alreadySelectedCount++;
          return;
        }

        // Find VM
        const vm = vmMap.get(id);
        if (vm) {
          newSelectedVMs.push(vm);
          addedCount++;
        } else {
          notFoundCount++;
        }
      });

      setSelectedVM(newSelectedVMs);
      
      // Update status message
      let message = "";
      if (addedCount > 0) message += `${addedCount} VM(s) added successfully. `;
      if (alreadyInCohortCount > 0) message += `${alreadyInCohortCount} already in cohort. `;
      if (alreadySelectedCount > 0) message += `${alreadySelectedCount} already selected. `;
      if (notFoundCount > 0) message += `${notFoundCount} not found.`;

      setPasteStatus({
        severity: addedCount > 0 ? "success" : "warning",
        message: message.trim()
      });

      setSearchLoading(false);
      
      // Trigger search to update filtered list
      debouncedSearch(pasteData);

      // Clear status after 5 seconds
      setTimeout(() => {
        setPasteStatus(null);
      }, 5000);
    }, 0);
  }, [allVMs, existingVMIds, selectedVM, debouncedSearch]);

  // Handle VM selection - only for new VMs, existing ones cannot be deselected
  const handleVMSelection = useCallback((machine, isSelected) => {
    // Check if this is an existing VM (already in cohort)
    const isExistingVM = existingVMs.some(vm => vm.id === machine.id);
    
    if (isExistingVM) {
      // Existing VMs cannot be selected/deselected
      toastr.warning("Warning", "This VM is already in the cohort and cannot be modified");
      return;
    }

    setSelectedVM(prev => {
      if (isSelected) {
        return [...prev, machine];
      } else {
        return prev.filter(vm => vm.id !== machine.id);
      }
    });
  }, [existingVMs]);

  // Clear VMs from last paste
  const handleClearAllFromLastPaste = useCallback(() => {
    if (lastPasteIds.length === 0) {
      toastr.info("Info", "No paste history found to clear");
      return;
    }

    const pasteIdsSet = new Set(lastPasteIds);
    const updatedSelectedVM = selectedVM.filter(vm => 
      !pasteIdsSet.has(vm.id.toString())
    );
    
    const clearedCount = selectedVM.length - updatedSelectedVM.length;
    
    setSelectedVM(updatedSelectedVM);
    setLastPasteIds([]);
    
    if (clearedCount > 0) {
      toastr.success("Success", `Cleared ${clearedCount} VM(s) from last paste`);
    } else {
      toastr.info("Info", "No VMs from last paste found in selection");
    }
  }, [lastPasteIds, selectedVM]);

  // Clear all selected VMs (only new selections, not existing ones)
  const handleClearAllSelected = useCallback(() => {
    setSelectedVM([]);
    setLastPasteIds([]);
    toastr.success("Success", "All selected VMs cleared");
  }, []);

  // Get only new VMs to be added (excluding existing ones)
  const getNewVMsToAdd = useCallback(() => {
    return selectedVM.filter(vm => 
      !existingVMIds.includes(vm.id.toString())
    );
  }, [selectedVM, existingVMIds]);

  // Get all VMs that should show as checked (existing + new selections)
  const getAllCheckedVMs = useCallback(() => {
    return [...existingVMs, ...selectedVM];
  }, [existingVMs, selectedVM]);

  // Handle update VMs API - only send new VMs
  const handleUpdateVM = useCallback(async () => {
    const newVMsToAdd = getNewVMsToAdd();
    
    if (newVMsToAdd.length === 0) { 
      setErrors(prev => ({
        ...prev,
        selectedVM: "Please select new VMs to add to the cohort", 
      }));
      return;  
    }

    try {
      const url = `/dashboard/api/v2/admin/vendingmachines/vm-cohorts/${id}/machines`;
      const requestBody = newVMsToAdd.map(machine => machine.id);

      console.log("API Payload (new VMs only):", requestBody);
      console.log("Total new VMs to add:", newVMsToAdd.length);

      props.getDataFromAPI(
        url,
        "POST",
        requestBody,
        (response) => {
          if (response?.status === "failure") {
            toastr.error("Error", response.message);
            return;
          }
          
          setSelectedVM([]);
          setErrors({});
          setLastPasteIds([]);
          toastr.success("Success", `${newVMsToAdd.length} VM(s) added successfully!`);
          navigate(-1);
        },
        (err) => {
          toastr.error("API Error", err.message || "Something went wrong");
        }
      );
    } catch (err) {
      console.log("Cohort create API error:", err.message);
      toastr.error("Unexpected Error", err.message || "An error occurred");
    }
  }, [selectedVM, id, navigate, props.getDataFromAPI, getNewVMsToAdd]);

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <CohortAddVmComponent
      loading={loading}
      seedData={seedData}
      errors={errors}
      handleVMSelection={handleVMSelection}
      handleUpdateVM={handleUpdateVM}
      selectedVM={selectedVM}
      cohortDetailsData={cohortDetailsData}
      searchLoading={searchLoading}
      handleSearchInputChange={handleSearchInputChange}
      vmSearchInput={vmSearchInput}
      handleBulkVMPaste={handleBulkVMPaste}
      pasteStatus={pasteStatus}
      filteredTableData={filteredTableData}
      handleClearSearch={handleClearSearch}
      handleClearAllFromLastPaste={handleClearAllFromLastPaste}
      handleClearAllSelected={handleClearAllSelected}
      tableContainerRef={tableContainerRef}
      getNewVMsToAdd={getNewVMsToAdd}
      getAllCheckedVMs={getAllCheckedVMs}
      existingVMs={existingVMs}
    />
  );
};

function mapStateToProps(state) {
  return {
    props: state,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(CohortAddVMContainer);